import React, { useEffect, useState } from 'react';
import {
    PearlError,
    PearlForm,
    PearlModalSizes,
    usePearlModal,
    useWindowSize,
    WebstoreImageSlider,
    WebstoreNavigation,
} from 'framework';
import { GhostButton, SolidButton, TextInput } from 'dbi-pearl-ui-kit';
import { useTranslation } from 'react-i18next';
import StyledCrawledVendorDetailsPage from './CrawledVendorDetailsPage.styled';
import { StorageKeys, StorageService } from 'lib/StorageService';
import TooltipComponent from 'event-user/components/TooltipComponent/TooltipComponent';
import { equifaxService } from 'lib/apis/equifax';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from 'shared/constants/navigation';
import { useVendorProfileViewTracker } from 'shared/hooks/useVendorProfileViewTracker';

const CrawledVendorDetailsPage = () => {
    const storage = StorageService();
    let vendorDetails = null;
    try {
        const crawledVendor = storage.get(StorageKeys.crawledVendor);
        vendorDetails = crawledVendor ? JSON.parse(crawledVendor) : null;
    } catch (e) {
        console.error('Invalid vendor JSON in storage', e);
        vendorDetails = null;
    }

    useVendorProfileViewTracker(vendorDetails?.id || vendorDetails?.placeId || null, {
        vendor_id: vendorDetails?.id,
        place_id: vendorDetails?.placeId,
        vendor_name: vendorDetails?.name,
        vendor_category: vendorDetails?.category,
        is_claimed: false,
        is_cyb: false,
        is_national: false,
        location:
            [vendorDetails?.city, vendorDetails?.state].filter(Boolean).join(', ') || undefined,
    });

    const { openModal, closeModal } = usePearlModal();
    const { width } = useWindowSize();
    const { t } = useTranslation();

    const EnterEinNumber = () => {
        const [success, setSuccess] = useState(false);
        const [showError, setShowError] = useState(false);

        const { getValues } = useFormContext();
        const enteredTinNumber = getValues('EINNumber');
        const navigate = useNavigate();

        if (!vendorDetails) {
            return <div>No vendor data found</div>;
        }

        const storeData = {
            EINNumber: enteredTinNumber,
            WebstoreName: 'FAIRFAX CHRISTIAN SCHOOL INC',
            ServiceAddressStreet: '123 Street',
            ServiceAddressCity: vendorDetails?.city,
            ServiceAddressState: vendorDetails?.state,
            ServiceAddressZip: '10001',
            placeId: vendorDetails?.placeId,
            crawledVendorId: vendorDetails?.id,
        };

        const handleVerification = async (storeData) => {
            try {
                const { data } = await equifaxService().verifyBusiness(storeData);
                if (data?.tinValidationResult?.validationPass === 'true') {
                    setSuccess(true);
                    setShowError(false);
                } else if (data?.tinValidationResult?.validationPass === 'false') {
                    setSuccess(false);
                    setShowError(true);
                }
            } catch (error) {
                console.log(error);
                setShowError(true);
            }
        };

        const handleContinue = () => {
            closeModal();
            navigate(APP_ROUTES.vendorNew);
        };

        return (
            <div style={{ color: 'black' }}>
                <h3 style={{ margin: '0px', textTransform: 'capitalize', marginBottom: '8px' }}>
                    {t('Pages.VendorDetailsPage.ClaimYourBusiness')}
                </h3>
                <p>{t('Pages.VendorDetailsPage.TooltipSubtitle')}</p>
                <TextInput formFieldName="EINNumber" />
                <PearlError name="EINNumber" />
                <p style={{ margin: '0px', marginBottom: '24px' }}>
                    {t('Pages.VendorDetailsPage.UseEIN')}{' '}
                    <a href="/about-us/contact" style={{ color: 'black' }}>
                        {t('Pages.VendorDetailsPage.ContactSupport')}
                    </a>{' '}
                </p>
                {!success && (
                    <SolidButton
                        style={{ width: ' -webkit-fill-available' }}
                        onClick={() => handleVerification(storeData)}
                    >
                        {t('Pages.VendorDetailsPage.VerifyAndClaim')}
                    </SolidButton>
                )}
                {success && (
                    <>
                        <SolidButton
                            style={{ width: ' -webkit-fill-available' }}
                            onClick={() => handleContinue()}
                        >
                            {t('Pages.VendorDetailsPage.Continue')}
                        </SolidButton>
                        <p style={{ color: 'green', marginTop: '10px', textAlign: 'center' }}>
                            {t('Pages.VendorDetailsPage.ClaimSuccessful')}
                        </p>
                    </>
                )}
                {showError && (
                    <p style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>
                        <div>{t('Pages.VendorDetailsPage.ValidationError')}</div>
                        {t('Pages.VendorDetailsPage.InvalidEIN')}{' '}
                        <a href="/about-us/contact">
                            {t('Pages.VendorDetailsPage.ContactSupport')}
                        </a>
                    </p>
                )}
            </div>
        );
    };

    const handleOpenModal = () => {
        openModal(
            <PearlForm formName="EIN">
                <EnterEinNumber />
            </PearlForm>,
            width < 786 ? PearlModalSizes.LG : PearlModalSizes.SM
        );
    };

    useEffect(() => {
        return () => {
            closeModal();
        };
    }, []);

    return (
        <StyledCrawledVendorDetailsPage>
            <WebstoreImageSlider webstore={''} isVendorPage={true} />
            <WebstoreNavigation />
            <div className="category-location-style">
                <h2 style={{ textTransform: 'none' }}>{vendorDetails?.name}</h2>
                <span style={{ position: 'relative', display: 'none' }}>
                    <span style={{ marginTop: '8px' }}>
                        {t('Pages.VendorDetailsPage.Unclaimed')}
                    </span>
                    <span>
                        <TooltipComponent
                            header={t('Pages.VendorDetailsPage.TooltipTitle')}
                            showCloseButton={false}
                            width={'355'}
                            height={'236'}
                            bottomSmall={'-675'}
                            leftSmall={width < 500 ? '220' : '445'}
                            left={'446'}
                            bottom={'-540'}
                            arrowBottom={'99'}
                            arrowLeft={'9'}
                            arrowLeftSmallscreen={width < 500 ? '32' : '9'}
                            arrowBottomSmallscreen={'99.5'}
                            content={t('Pages.VendorDetailsPage.TooltipSubtitle')}
                            showArrowBorder={true}
                            removeTopPadding={true}
                            redHeader={true}
                            showButton={true}
                            handleButton={() => handleOpenModal()}
                        />
                    </span>
                </span>
                <p>
                    <b>
                        {t('Pages.VendorDetailsPage.Category')} {vendorDetails?.category}
                    </b>
                </p>
                <p>
                    <b>
                        {t('Pages.VendorDetailsPage.Location')}{' '}
                        {vendorDetails?.city + ', ' + vendorDetails?.state}
                    </b>
                    <div
                        className="CYBFooter"
                        data-testid="CYB-component"
                        style={{ display: 'none' }}
                    >
                        <h4 className="CYBtitle">{t('Pages.VendorDetailsPage.CYBFooterTitle')}</h4>
                        {t('Pages.VendorDetailsPage.CYBFooterSubtitle')}
                    </div>
                    <GhostButton
                        className="button-style"
                        style={{ display: 'none' }}
                        onClick={() => handleOpenModal()}
                    >
                        {t('Pages.VendorDetailsPage.CYBFooterButton')}
                    </GhostButton>
                </p>
            </div>
        </StyledCrawledVendorDetailsPage>
    );
};

export default CrawledVendorDetailsPage;
